import { memo, useState, useContext, useEffect, useCallback, type FunctionComponent, type MouseEvent } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import transform from 'lodash/transform';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import isBoolean from 'lodash/isBoolean';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
// Skillmore UI Components
import { getCurrentSeconds } from '@empathco/ui-components/src/helpers/datetime';
import usePolling, { POLLING_FETCH_POLICY } from '@empathco/ui-components/src/hooks/usePolling';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import SkillsLegend from '@empathco/ui-components/src/elements/SkillsLegend';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import ActionSucceededMessage from '@empathco/ui-components/src/elements/ActionSucceededMessage';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { LINKEDIN_PROFILE_IMPORT_STATUS_QUERY } from '~/graphql/LinkedinProfileImportStatus';
import { LINKEDIN_PROFILE_IMPORT } from '../graphql/LinkedinProfileImport';
import {
  LinkedinProfileImportStatus,
  LinkedinProfileImportStatusDocument, LinkedinProfileImportStatusQuery,
  LinkedinProfileImportDocument
} from '../graphql/types';
import { Skill, SkillLevel, SKILL_LEVEL_FIRST } from '../models/skill';
import { STEP_CURRENT_JOB, STEP_TARGET, STEP_SCOPE, STEP_ADDITION } from '../constants/builder';
import useCustomerSettings from '../config/customer';
import { POLLING_INTERVAL } from '../config/params';
import { API_MY_RESUME_STATUS } from '../config/api';
import { PREF_SKILL_FIELDS, getSkillCurrentLevel /* , getSkillCurrentLevelOnly */ } from '../helpers/models';
import useStatusPoller from '../hooks/useStatusPoller';
import { SkillLevelData } from '../context/dataContext';
import { GlobalContext } from '../context/global';
import { DataContext } from '../context';
import BuilderSubheader from '../elements/BuilderSubheader';
import BuilderFooter from '../elements/BuilderFooter';
import SkillsGrid from '../v3/SkillsGrid';
import AddSkillDialog from '../widgets/AddSkillDialog';

type LinkedinStatusFailure = 'fetch' | 'processing';

type BuilderInferredStepProps = {
  readonly step: number;
  readonly onNext: (confirmed: boolean) => void;
  readonly onUploadStart?: () => void;
  readonly onUploadEnd?: () => void;
  readonly disabled?: boolean;
  readonly resumeUploading?: boolean;
  // for Storybook only
  readonly skipResumeCheck?: boolean;
  readonly testResume?: boolean;
  readonly testLinkedin?: boolean;
}

const BuilderInferredStep: FunctionComponent<BuilderInferredStepProps> = ({
  step,
  onNext,
  onUploadStart,
  onUploadEnd,
  disabled: parentDisabled = false,
  resumeUploading = false,
  skipResumeCheck = false,
  testResume,
  testLinkedin
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}) => {
  const { HAS_MENTORING, HAS_RESUME_UPLOADER, HAS_RESUME_LINKEDIN } = useCustomerSettings();
  const hasResumeUploader = HAS_RESUME_UPLOADER && step === STEP_ADDITION;
  const hasResumeLinkedin = HAS_RESUME_LINKEDIN && step === STEP_ADDITION;

  const { cache: apolloCache } = useApolloClient();

  const { user: { data: user } } = useContext(GlobalContext);
  const {
    editableSks: { data: editableSks, pending, failed: failedSks }, requireEditableSkills,
    preferences: { pending: preferencesPending, failed: preferencesFailed },
    requirePreferences, // required by AddSkillDialog -> EditSkillLevel
    skillsUpdate: { pending: updatePending, failed: updateFailed }, updateSkills,
    skillUpdate: { pending: updateSkillPending, failed: updateSkillFailed, params: updateSkillParams }, updateSkill
  } = useContext(DataContext);
  const scope = step >= STEP_CURRENT_JOB && step < STEP_TARGET ? STEP_SCOPE[step - 1] : null;

  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [exclude, setExclude] = useState<number[]>(map(editableSks, 'id'));
  const [anchorAddBtn, setAnchorAddBtn] = useState<HTMLButtonElement | null>(null);

  const [linkedinJobPending, setLinkedinJobPending] = useState(testLinkedin === false);
  const {
    mutate: importLinkedIn, loading: linkedinPending, failed: linkedinFailed, succeeded: linkedinSucceeded
  } = useMutationMethod({
    key: 'linkedinProfileImport',
    mutation: useMutation(LINKEDIN_PROFILE_IMPORT as typeof LinkedinProfileImportDocument)
  });
  // lazy load LinkedIn Profile Import Status
  const linkedinStatusQuery = useLazyQuery(
    LINKEDIN_PROFILE_IMPORT_STATUS_QUERY as typeof LinkedinProfileImportStatusDocument
  );
  const {
    pending: pendingLinkedinStatus, failed: failedLinkedinStatus, results: linkedinStatus
  } = useQueryObject({
    data: undefined as unknown as LinkedinProfileImportStatusQuery['linkedinProfileImportStatus'],
    key: 'linkedinProfileImportStatus',
    flatResults: true,
    lazyQuery: linkedinStatusQuery
  });
  const [execLinkedinStatus] = linkedinStatusQuery;
  // cleared before every poll, so an unchanged outcome still transitions its snackbar open
  const [linkedinStatusFailure, setLinkedinStatusFailure] = useState<LinkedinStatusFailure | null>(null);

  const pollLinkedinStatus = useCallback((options: Parameters<typeof execLinkedinStatus>[0]) => {
    setLinkedinStatusFailure(null);
    (async () => {
      try {
        const { data, error } = await execLinkedinStatus(options);
        if (error) setLinkedinStatusFailure('fetch');
        else if (data?.linkedinProfileImportStatus?.run_status === LinkedinProfileImportStatus.error) {
          setLinkedinStatusFailure('processing');
        }
      } catch (_err) {
        setLinkedinStatusFailure('fetch');
      }
    })();
  }, [execLinkedinStatus]);
  const linkedinStatusPending = linkedinStatus?.run_status
    ? linkedinStatus.run_status !== LinkedinProfileImportStatus.complete &&
      linkedinStatus.run_status !== LinkedinProfileImportStatus.error
    : false;
  const linkedinStatusError = linkedinStatus?.run_status
    ? linkedinStatus.run_status === LinkedinProfileImportStatus.error
    : false;
  const linkedinStatusCompleted = linkedinStatus?.run_status
    ? linkedinStatus.run_status === LinkedinProfileImportStatus.complete
    : false;
  const pollingLinkedinStatus = usePolling(
    linkedinJobPending || linkedinStatusPending,
    failedLinkedinStatus,
    linkedinStatusPending || linkedinJobPending,
    POLLING_INTERVAL
  );
  const [linkedinReloadSkills, setLinkedinReloadSkills] = useState(linkedinStatusCompleted);
  useEffect(() => {
    if (linkedinStatusCompleted) setLinkedinReloadSkills(true);
  }, [linkedinStatusCompleted]);

  const [resumeJobId, setResumeJobId] = useState(step === STEP_ADDITION ? getCurrentSeconds() : undefined);
  const {
    data: resumeStatus, computed: resumeComputed, computing: resumeComputing,
    pending: resumeStatusPending, failed: resumeJobFailed
  } = useStatusPoller(step === STEP_ADDITION && !skipResumeCheck ? resumeJobId : undefined, API_MY_RESUME_STATUS, true);
  const resumeStatusEmpty = resumeStatus && isEmpty(resumeStatus) && resumeComputing;
  const resumeJobPending = (!resumeStatusEmpty && resumeComputing) || resumeStatusPending;
  const resumePending = hasResumeUploader && (resumeJobPending || testResume) ? true : undefined;

  const linkedinImportPending = hasResumeLinkedin &&
    (linkedinPending || linkedinJobPending || linkedinStatusPending) ? true : undefined;

  const loading = pending || !editableSks || !skills || (step === STEP_ADDITION && preferencesPending);
  const failed = failedSks || (step === STEP_ADDITION && preferencesFailed);
  const disabled = parentDisabled || loading || failed || updatePending || updateSkillPending ||
    (hasResumeUploader && (resumeUploading || resumePending)) ||
    (hasResumeLinkedin && (pendingLinkedinStatus || linkedinImportPending)) ||
    !updateSkills || !updateSkill;

  // load LinkedIn Profile Import Status
  useEffect(() => {
    if (hasResumeLinkedin && pollingLinkedinStatus >= 0) pollLinkedinStatus({
      ...pollingLinkedinStatus >= 1 ? POLLING_FETCH_POLICY : {}
    });
  }, [pollingLinkedinStatus, hasResumeLinkedin, pollLinkedinStatus]);

  useEffect(() => {
    if (hasResumeLinkedin && scope && (failedLinkedinStatus || linkedinStatusError || linkedinReloadSkills)) {
      if (!failedLinkedinStatus && linkedinReloadSkills) {
        requireEditableSkills?.({ inferred: true, scope, forced: true });
        setLinkedinReloadSkills(false);
      }
      setLinkedinJobPending(false);
    }
  }, [failedLinkedinStatus, linkedinReloadSkills, linkedinStatusError, scope, hasResumeLinkedin, requireEditableSkills]);

  useEffect(() => {
    if (hasResumeUploader && scope && !resumeJobPending && resumeComputed) {
      requireEditableSkills?.({ inferred: true, scope, forced: true });
      setResumeJobId(undefined);
    }
  }, [resumeComputed, resumeJobPending, scope, hasResumeUploader, requireEditableSkills]);

  useEffect(() => {
    if (scope) requireEditableSkills?.({ inferred: true, scope });
  }, [scope, requireEditableSkills]);

  useEffect(() => {
    if (resumeStatusEmpty) setResumeJobId(undefined);
  }, [resumeStatusEmpty]);

  useEffect(() => {
    if (step === STEP_ADDITION) requirePreferences?.();
  }, [step, requirePreferences]);

  useEffect(() => {
    if (!editableSks) {
      setSkills(null);
      setExclude([]);
      setAnchorAddBtn(null);
    } else if (editableSks && (!skills || skills !== editableSks)) {
      const updatedSkills = uniqBy([...skills || [], ...editableSks], 'id');
      setSkills(updatedSkills);
      setExclude(map(updatedSkills, 'id'));
      setAnchorAddBtn(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableSks]); // do not need `skills` dependency, because we should do nothing here if it updates

  const updateLevel = useCallback((id: number, level: SkillLevel, mentoring: boolean, isTarget?: boolean) => {
    const index = findIndex(skills, ['id', id]);
    const skill = index >= 0 ? (skills as Skill[])[index] : null;
    const { actual_level, current_level, is_mentoring, is_target, is_inference_newer } = skill || {};
    if (skill && (
      is_inference_newer ||
      actual_level !== level ||
      current_level !== level ||
      (HAS_MENTORING && is_mentoring !== mentoring) ||
      (isBoolean(isTarget) && is_target !== isTarget)
    )) {
      const updatedSkills = [...skills as Skill[]];
      updatedSkills[index] = {
        ...skill,
        is_inference_newer: false,
        current_level: level,
        actual_level: level,
        ...HAS_MENTORING ? { is_mentoring: mentoring } : {},
        is_my: level >= SKILL_LEVEL_FIRST,
        ...isBoolean(isTarget) ? { is_target: isTarget } : {}
      };
      // update skill right away (do not wait for Skip or Confirm All button click)
      updateSkill?.({
        apolloCache,
        skill_id: id,
        level,
        ...HAS_MENTORING ? { is_opt_in_mentor: mentoring } : {},
        ...isBoolean(isTarget) ? { is_target: isTarget } : {},
        source: 'profile_builder'
      });
      setSkills(updatedSkills);
    }
  }, [skills, updateSkill, apolloCache, HAS_MENTORING]);

  const handleAddSkill = useCallback((skl: Skill, level: SkillLevel, mentoring: boolean, is_target?: boolean) => {
    if (findIndex(skills, ['id', skl.id]) < 0) {
      if (level >= SKILL_LEVEL_FIRST) {
        // add new skill with specified level
        setSkills([
          ...skills || [],
          {
            ...skl,
            is_inference_newer: false,
            current_level: level,
            actual_level: level,
            ...HAS_MENTORING ? { is_mentoring: mentoring } : {},
            ...isBoolean(is_target) ? { is_target } : {},
            is_my: true
          }
        ]);
        setExclude((prevAdded) => uniq([...prevAdded, skl.id]));
        // save added skill right away (do not wait for Skip or Confirm All button click)
        updateSkill?.({
          apolloCache,
          skill_id: skl.id,
          level,
          ...HAS_MENTORING ? { is_opt_in_mentor: mentoring } : {},
          ...isBoolean(is_target) ? { is_target } : {},
          source: 'profile_builder',
          skill: pick(skl, PREF_SKILL_FIELDS)
        });
      }
    } else updateLevel(skl.id, level, mentoring, is_target);
    setAnchorAddBtn(null);
  }, [skills, updateLevel, updateSkill, apolloCache, HAS_MENTORING]);

  const handleAddOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => event && setAnchorAddBtn(event.currentTarget), []);
  const handleAddClose = useCallback(() => setAnchorAddBtn(null), []);

  const handleLinkedIn = useCallback((profileId: string) => {
    setLinkedinStatusFailure(null);
    importLinkedIn?.({
      variables: { input: { linkedin_profile_id: profileId } },
      onCompleted(data) {
        if (data.linkedinProfileImport?.success) setLinkedinJobPending(true);
      }
    });
  }, [importLinkedIn]);

  const handleLevelUpdates = useCallback((skip: boolean) => {
    if (skip) {
      onNext(false);
      return;
    }
    if (scope) {
      const updatedLevels = transform(skills as Skill[], (levels, skill) => {
        const { id, is_mentoring, is_target } = skill;
        const level = getSkillCurrentLevel(skill);
        levels.push({
          skill_id: id,
          level,
          ...HAS_MENTORING ? { is_opt_in_mentor: Boolean(is_mentoring) } : {},
          ...isBoolean(is_target) ? { is_target } : {}
        });
      }, [] as SkillLevelData[]);

      if (size(updatedLevels) < 1) onNext(true);
      else updateSkills?.({
        apolloCache,
        onSuccess: () => onNext(true),
        is_onboarding: true,
        onboarding_step: scope,
        levels: updatedLevels
      });
    }
  }, [skills, scope, updateSkills, onNext, apolloCache, HAS_MENTORING]);

  const handleSkip = useCallback(() => handleLevelUpdates(true), [handleLevelUpdates]);
  const handleConfirm = useCallback(() => handleLevelUpdates(false), [handleLevelUpdates]);

  const handleUpload = useCallback(() => {
    setResumeJobId(getCurrentSeconds());
  }, []);
  const handleOK = useCallback(() => {
    // no-op
  }, []);

  useEffect(() => {
    if (testLinkedin) importLinkedIn?.({
      variables: { input: { linkedin_profile_id: 'test' } },
      onCompleted(data) {
        if (data.linkedinProfileImport?.success) setLinkedinJobPending(true);
      }
    });
  }, [testLinkedin, importLinkedIn]);

  const skillsCount = size(skills);

  const content = (
    <SkillsGrid
        isEmployee
        source="profile_builder"
        notFoundMessage={step !== STEP_ADDITION ||
          user?.has_current_job_skills || user?.has_in_demand_skills ? `builder.step${step}.empty` : undefined}
        skills={skills}
        failed={failed}
        pending={loading}
        plain
        removeIcon="all"
        onItemUpdate={updateLevel}
        disabled={disabled}
        updatingSkillId={(updateSkillPending && updateSkillParams?.skill_id) || null}
        filters={!failed && !loading && skillsCount >= 1 ? <SkillsLegend/> : undefined}
    />
  );

  return (
    <>
      <BuilderSubheader
          key={step}
          user={user}
          title={`builder.step${step}.subheader`}
          info={`builder.step${step}.info`}
          infoLink={step === 2 ? 'builder.step2.help.button' : undefined}
          infoText={step === 2 ? 'builder.step2.help.text' : undefined}
          disabled={disabled}
          onAdd={step === STEP_ADDITION ? handleAddOpen : undefined}
          onLinkedIn={hasResumeLinkedin ? handleLinkedIn : undefined}
          onUpload={hasResumeUploader ? handleUpload : undefined}
          onUploadStart={onUploadStart}
          onUploadEnd={onUploadEnd}
          pending={loading}
          pendingResume={resumePending}
          pendingLinkedIn={linkedinImportPending}
          testUploading={testResume === false ? true : undefined}
      />
      {hasResumeUploader || hasResumeLinkedin ? (
        <ReloadingOverlay reloading={((resumePending || linkedinImportPending) && !loading) || resumeUploading}>
          {content}
        </ReloadingOverlay>
      ) : content}
      <BuilderFooter
          nextCaption={((loading || skillsCount > 1) && 'builder.button.confirm_all') ||
            (skillsCount < 1 && step !== STEP_ADDITION && 'builder.button.next') || undefined}
          topPadding={loading || resumePending || failed ? true : undefined}
          disabledPrev={parentDisabled || loading || updatePending || updateSkillPending ? true : undefined}
          disabledNext={disabled}
          pendingNext={updatePending ? true : undefined}
          onPrev={handleSkip}
          onNext={handleConfirm}
      />
      <ActionSnackbar
          severity="error"
          message="edit_skills.submit_error"
          open={updateFailed}
      />
      <ActionSnackbar
          severity="error"
          message="skill.level_update_error"
          open={updateSkillFailed}
      />
      {step === STEP_ADDITION && (
        <>
          <AddSkillDialog
              plain
              anchorEl={anchorAddBtn}
              exclude={exclude}
              onAdd={handleAddSkill}
              onCancel={handleAddClose}
              disabled={disabled}
          />
          {hasResumeUploader ? (
            <ActionSnackbar
                severity="error"
                open={resumeJobFailed}
                message="builder.resume.error_processing"
            />
          ) : undefined}
          {hasResumeLinkedin ? (
            <>
              <ActionSnackbar
                  severity="error"
                  open={linkedinFailed || linkedinStatusFailure === 'fetch'}
                  message="builder.linkedin.error"
              />
              <ActionSnackbar
                  severity="error"
                  open={linkedinStatusFailure === 'processing'}
                  message="builder.linkedin.error_processing"
              />
              <ActionSucceededMessage
                  open={linkedinSucceeded}
                  title="builder.linkedin.success.title"
                  message="builder.linkedin.success.message"
                  onOK={handleOK}
                  compact
              />
            </>
          ) : undefined}
        </>
      )}
    </>
  );
};

export default memo(BuilderInferredStep);
